import { mutationOptions, queryOptions } from "@tanstack/react-query";
import { partnerAdminKeys } from "@/constant/queryKeys";
import { apiRequestV2, queryClient } from "@/lib/queryClient";
import type {
  FeaturingPartnerAdmin,
  PaginatedLike,
  PartnerAdmin,
  PartnerCategoryAdmin,
  PartnerCollectionItemAdmin,
  PartnerEggAdmin,
  PartnerProject,
  PartnerQuestAdmin,
  PartnerRarityAdmin,
  PartnerTokenListItem,
} from "@/types/partner-management";

function invalidatePartnerAdmin() {
  queryClient.invalidateQueries({ queryKey: partnerAdminKeys.all });
}

function unwrap<T>(response: T | { data: T }): T {
  return response && typeof response === "object" && "data" in response
    ? (response as { data: T }).data
    : (response as T);
}

export function getPartnerAdminListOptions(params: { search?: string }) {
  return queryOptions({
    queryKey: partnerAdminKeys.partners(params),
    queryFn: async () => {
      const partners = await apiRequestV2<PartnerAdmin[]>("GET", "/api/v1/partner");
      const search = params.search?.trim().toLowerCase();
      return search
        ? partners.filter((partner) =>
            [partner.name, partner.slug].some((value) => value?.toLowerCase().includes(search)),
          )
        : partners;
    },
  });
}

export function getPartnerAdminOptions(id?: number | null) {
  return queryOptions({
    queryKey: partnerAdminKeys.partner(id),
    queryFn: () => apiRequestV2<PartnerAdmin>("GET", `/api/v1/partner/${id}`),
    enabled: Number.isFinite(id),
  });
}

export function getPartnerProjectsOptions(partnerId?: number | null) {
  return queryOptions({
    queryKey: partnerAdminKeys.projects(partnerId),
    queryFn: () => apiRequestV2<PartnerProject[]>("GET", `/api/v1/partner/${partnerId}/projects`),
    enabled: Number.isFinite(partnerId),
  });
}

export function getPartnerQuestsOptions(partnerId?: number | null) {
  return queryOptions({
    queryKey: partnerAdminKeys.quests(partnerId),
    queryFn: () => apiRequestV2<PartnerQuestAdmin[]>("GET", `/api/v1/quest/partner/${partnerId}`),
    enabled: Number.isFinite(partnerId),
  });
}

export function getPartnerEggsOptions(partnerId?: number | null) {
  return queryOptions({
    queryKey: partnerAdminKeys.eggs(partnerId),
    queryFn: async () =>
      (await apiRequestV2<PaginatedLike<PartnerEggAdmin>>("GET", `/api/v1/egg/partner/${partnerId}`)).data,
    enabled: Number.isFinite(partnerId),
  });
}

export function getPartnerCollectionItemsOptions(partnerId?: number | null, filters?: Record<string, string>) {
  const params = new URLSearchParams();
  Object.entries(filters ?? {}).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  const query = params.toString();
  return queryOptions({
    queryKey: partnerAdminKeys.items(partnerId, filters),
    queryFn: async () =>
      (await apiRequestV2<PaginatedLike<PartnerCollectionItemAdmin>>(
        "GET",
        `/api/v1/collection-item/partner/${partnerId}${query ? `?${query}` : ""}`,
      )).data,
    enabled: Number.isFinite(partnerId),
  });
}

export function getPartnerRaritiesOptions(partnerId?: number | null) {
  return queryOptions({
    queryKey: partnerAdminKeys.rarities(partnerId),
    queryFn: () => apiRequestV2<PartnerRarityAdmin[]>("GET", `/api/v1/rarity/partner/${partnerId}`),
    enabled: Number.isFinite(partnerId),
  });
}

export function getPartnerCategoriesOptions(partnerId?: number | null) {
  return queryOptions({
    queryKey: partnerAdminKeys.categories(partnerId),
    queryFn: async () =>
      (await apiRequestV2<PaginatedLike<PartnerCategoryAdmin>>("GET", `/api/v1/category/partner/${partnerId}`)).data,
    enabled: Number.isFinite(partnerId),
  });
}

export function getTokenListOptions() {
  return queryOptions({
    queryKey: partnerAdminKeys.tokenList,
    queryFn: async () =>
      (await apiRequestV2<PaginatedLike<PartnerTokenListItem>>("GET", "/api/v1/token-list")).data,
  });
}

export function getFeaturingPartnersOptions() {
  return queryOptions({
    queryKey: partnerAdminKeys.featuringPartners,
    queryFn: async () =>
      (await apiRequestV2<PaginatedLike<FeaturingPartnerAdmin>>("GET", "/api/v1/featuring-partner")).data,
  });
}

export function getEggPoolOptions(eggId?: number | null) {
  return queryOptions({
    queryKey: partnerAdminKeys.eggPool(eggId),
    queryFn: async () =>
      (await apiRequestV2<{ data: PartnerCollectionItemAdmin[] }>("GET", `/api/v1/egg/${eggId}/collection-items`)).data,
    enabled: Number.isFinite(eggId),
  });
}

export function partnerAdminMutationOptions<TPayload, TResult = unknown>(method: string, url: string) {
  return mutationOptions<TResult, Error, TPayload>({
    mutationFn: (data) => apiRequestV2<TResult>(method, url, data),
    onSuccess: invalidatePartnerAdmin,
  });
}

export function uploadAdminImage(file: File, bucket: "partner" | "timeline" | "egg" | "nft" | "rarity" | "token", folder?: string, filenameHint?: string) {
  const form = new FormData();
  form.append("file", file);
  form.append("bucket", bucket);
  if (folder) form.append("folder", folder);
  if (filenameHint) form.append("filenameHint", filenameHint);
  return apiRequestV2<{ url: string; objectName: string }>("POST", "/api/v1/upload/image", form);
}
